import { P2Accordion, P2Alert, P2TextDescription, P2TextLabel } from 'react-bootstrap-common-script';
import { Separator } from '../../../components/separator';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hook';
import { PaymentCardIcon } from '../../../assets/svg';
import { useNavigate } from 'react-router-dom';

import { StageEInfoCard } from '../../../components/stage-e-info-card';
import StatusTag from '../../../components/status-tag/status-tag';
import { Parties, WithdrawalRequests } from '../../../models/interfaces/stage-e.interface';
import { Helpers } from '../../../utils/helper';
import { resetSelectCpfFormUuid, resetSelectedForm, selectedForm, updateSelectCpfFormUuid } from '../../../redux/slices/application-stage-e-slice';
import { useGetCpfServiceNotifiedStatusQuery, useGetCpfWithdrawalOverviewQuery } from '../../../redux/services/getStageE';
import FinancialPlan from '../component/financial-plan';
import { AccordionStatus } from '../../../models/enum/status-tag.enum';
import { PolymerCrypto } from '../../../utils/encryptor';
import { LottieLoader } from '../../../components/loader';

interface CPFWithdrawalEServiceProps {
  index?: number;
}

enum CpfFormStatus {
  Pending = 'PENDING_APPLICANT',
  CompletedForm = 'COMPLETED_FORM',
  CpfSent = 'CPF_SENT',
  CpfResent = 'CPF_RESENT',
  CpfRejected = 'CPF_REJECTED',
  CpfAccepted = 'CPF_ACCEPTED',
  RefundRequired = 'REFUND_REQUIRED',
}

const groupRequestsByChangeId = (requests: WithdrawalRequests[]) => {
  interface GroupedRequests {
    [changeId: string]: {
      requests: WithdrawalRequests[];
      totalAmount: number;
      serialNumbers: string[];
      uuid: string[];
      latestUpdate: string;
      isOverride: boolean;
    };
  }

  return requests.reduce<GroupedRequests>((acc, request) => {
    const changeId = request.changeId;
    if (!acc[changeId]) {
      acc[changeId] = {
        requests: [],
        totalAmount: 0,
        serialNumbers: [],
        uuid: [],
        latestUpdate: new Date(0).toISOString(),
        isOverride: false,
      };
    }
    acc[changeId].requests.push(request);
    acc[changeId].totalAmount += request.withdrawalAmount; // Sum the amounts
    acc[changeId].serialNumbers.push(request.serialNumber); // Collect serial numbers
    acc[changeId].uuid.push(request.uuid); // Collect uuid
    acc[changeId].isOverride = acc[changeId].isOverride || request.isOverride;

    // Update the latestUpdate if this request is more recent
    if (new Date(request.lastUpdatedAt) > new Date(acc[changeId].latestUpdate)) {
      acc[changeId].latestUpdate = request.lastUpdatedAt; // Store the latest update date
    }

    return acc;
  }, {});
};

const CPFWithdrawalEService: React.FC<CPFWithdrawalEServiceProps> = ({ index }) => {
  let appDispatch = useAppDispatch();
  let navigate = useNavigate();
  const queryParameters = new URLSearchParams(window.location.search);
  const application_id = queryParameters.get('application_id');
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);

  const [isAccordionActive, setIsAccordionActive] = useState<boolean>(true);

  // Toggle the accordion subtitle when called.
  const handleAccordionSubtitle = useCallback(() => {
    setIsAccordionActive((prev) => !prev);
  }, []);
  const [statusTag, setStatusTag] = useState<AccordionStatus>();

  const { data: cpfWithdrawalOverview, isLoading: cpfWithdrawalOverviewLoading } = useGetCpfWithdrawalOverviewQuery({
    application_id: application_id ?? '',
    uinfin: loginUser.uinfin ?? '',
    singpass_token: singpassToken ?? '',
  });

  const { data: proposedOwnersNotifiedStatus, isLoading: proposedOwnersNotifiedStatusLoading } = useGetCpfServiceNotifiedStatusQuery({
    application_id: application_id ?? '',
    uinfin: loginUser.uinfin,
    singpass_token: singpassToken ?? '',
  });

  const loginOwnerCpfOverview = cpfWithdrawalOverview?.parties?.find((item: Parties) => item.nric === loginUser?.uinfin);
  const loginOwnerCpfOverviewPendingRequests = loginOwnerCpfOverview?.withdrawalRequests?.filter(
    (item: WithdrawalRequests) => item.status === CpfFormStatus.Pending
  );
  const loginOwnerCpfOverviewSuccessRequests = loginOwnerCpfOverview?.withdrawalRequests?.filter(
    (item: WithdrawalRequests) =>
      item.status === CpfFormStatus.CompletedForm ||
      item.status === CpfFormStatus.CpfSent ||
      item.status === CpfFormStatus.CpfResent ||
      item.status === CpfFormStatus.CpfAccepted
  );

  // Group by changeID(Batch), note that this might be a potential bug in the future. As am currently only expecting one changeId for pending.
  const groupedLoginOwnerPendingRequests = groupRequestsByChangeId(loginOwnerCpfOverviewPendingRequests || []);
  const groupedLoginOwnerSuccessRequests = groupRequestsByChangeId(loginOwnerCpfOverviewSuccessRequests || []);
  const isApplicantEServiceEnabled = !!cpfWithdrawalOverview?.applicantEServiceEnabled;

  // Other Owner's CpfWithdrawalOverview
  const otherOwnerCpfOverview = cpfWithdrawalOverview?.parties?.filter((item: Parties) => item.nric !== loginUser?.uinfin);
  const otherOwnerCpfRequests = otherOwnerCpfOverview?.map((owner) => {
    const filteredRequests = owner.withdrawalRequests.filter((item: WithdrawalRequests) => item.status === CpfFormStatus.Pending);
    const filteredSuccessRequests = owner.withdrawalRequests.filter(
      (item: WithdrawalRequests) =>
        item.status === CpfFormStatus.CompletedForm ||
        item.status === CpfFormStatus.CpfSent ||
        item.status === CpfFormStatus.CpfResent ||
        item.status === CpfFormStatus.CpfAccepted
    );
    return {
      nric: owner.nric,
      pendingRequests: groupRequestsByChangeId(filteredRequests),
      successRequests: groupRequestsByChangeId(filteredSuccessRequests),
    };
  });

  // Reset the CPF SN that will be used when E-Service is started
  useEffect(() => {
    appDispatch(resetSelectedForm());
    appDispatch(resetSelectCpfFormUuid());
  }, []);

  // Update Accordion Status
  useEffect(() => {
    const isPendingOtherOwner = otherOwnerCpfRequests?.some((owner) => Object.keys(owner.pendingRequests).length > 0);
    const isPendingLoginOwner = !!loginOwnerCpfOverviewPendingRequests?.length;
    if (isApplicantEServiceEnabled && isPendingLoginOwner) {
      setStatusTag(AccordionStatus.ActionRequired);
    } else if (isPendingLoginOwner) {
      setStatusTag(undefined);
    } else if (isPendingOtherOwner) {
      console.log(otherOwnerCpfRequests);
      setStatusTag(AccordionStatus.PendingOtherOwner);
    } else {
      setStatusTag(AccordionStatus.Completed);
    }
  }, [isApplicantEServiceEnabled, loginOwnerCpfOverviewPendingRequests, otherOwnerCpfRequests]);

  /**
   * Navigate to CPF e-Service form
   */
  function handleNavigate(item: string[], uuid: string[]) {
    appDispatch(selectedForm(item));
    appDispatch(updateSelectCpfFormUuid(uuid));
    navigate(`/application-coo/stage-e/cpf-e-service?application_id=${application_id}`);
  }

  if (cpfWithdrawalOverviewLoading || proposedOwnersNotifiedStatusLoading) {
    return <LottieLoader />;
  }
  return (
    <div className='polymer-stage-e-accordion'>
      <P2Accordion
        defaultOpen={true}
        hasDivider={true}
        onToggle={handleAccordionSubtitle}
        title={
          <div className='polymer-accordion-listing_title'>
            {loginOwnerCpfOverview && loginOwnerCpfOverview?.withdrawalRequests?.length > 0 && statusTag && (
              <div style={{ marginBottom: '0.5rem' }}>
                <StatusTag status={`${statusTag ?? ''}`} />
              </div>
            )}
            <div className={`polymer-accordion-listing_title_wrap ${isAccordionActive ? 'accordion-active' : ''}`}>
              {index ? <h4>{index}. CPF withdrawal e-service</h4> : <h4 style={{ color: 'black' }}>CPF withdrawal e-service</h4>}
            </div>
            {isAccordionActive && (
              <div className='polymer-stage-e-accordion-subtitle'>
                <P2TextDescription
                  color='#333'
                  description={
                    'Based on the confirmed financial plan, you will need to make the following withdrawal of your CPF savings and payments for your flat. Please complete the CPF withdrawal form before attending the completion appointment.'
                  }
                />
              </div>
            )}
          </div>
        }>
        {/* Accordion's Content Starts Here --- */}
        {/* Financial Plan */}
        <FinancialPlan />

        {/* Login Owner's Display Cards */}
        <div className='cpf-withdrawal-e-service-content'>
          <Separator variant='dashed' style={{ marginBottom: '1rem' }} />
          {loginOwnerCpfOverview && loginOwnerCpfOverview?.withdrawalRequests?.length > 0 && (
            <P2TextLabel bold={700} label={`CPF withdrawal by ${loginUser?.name}`} />
          )}

          {groupedLoginOwnerSuccessRequests &&
            Object.entries(groupedLoginOwnerSuccessRequests).map(([changeId, { totalAmount, latestUpdate }]) => (
              <div key={changeId} style={{ marginBottom: '1.5rem' }}>
                <StageEInfoCard
                  variant='success'
                  cardLabel={'Form to withdraw CPF savings has been completed'}
                  title={`$ ${Helpers.formatThousandSeparator(totalAmount)}`}
                  description={`Completed on: ${Helpers.formatDate(latestUpdate)}`}
                />
              </div>
            ))}

          {groupedLoginOwnerPendingRequests &&
            Object.entries(groupedLoginOwnerPendingRequests).map(([changeId, { totalAmount, serialNumbers, uuid, isOverride }]) => (
              <div key={changeId} style={{ marginBottom: '1.5rem' }}>
                <StageEInfoCard
                  variant='info'
                  firstTime={isOverride ? false : true}
                  cardLabel={'Amount to be withdrawn'}
                  title={`$ ${Helpers.formatThousandSeparator(totalAmount)}`}
                  description={''}
                  onClick={isApplicantEServiceEnabled ? () => handleNavigate(serialNumbers, uuid) : undefined}
                  buttonDisabled={!isApplicantEServiceEnabled}
                  icon={<PaymentCardIcon />}
                />
              </div>
            ))}
        </div>

        {loginOwnerCpfOverview && loginOwnerCpfOverview?.withdrawalRequests?.length && (
          <>
            {statusTag === AccordionStatus.PendingOtherOwner && (
              <div style={{ marginTop: '8px' }}>
                <P2Alert
                  variant='warning'
                  text='HDB will submit the CPF withdrawal forms for the whole household, after all the owners have completed their form.'
                />
              </div>
            )}
            {loginOwnerCpfOverview.withdrawalRequests.some((request) => request.status === CpfFormStatus.CpfRejected) &&
              loginOwnerCpfOverviewPendingRequests &&
              loginOwnerCpfOverviewPendingRequests.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <P2Alert
                    variant='danger'
                    text='CPFB has rejected the withdrawal form that you have submitted previously. Please complete and submit the withdrawal form again. '
                  />
                </div>
              )}
          </>
        )}

        {/* Draw seperator base on condition*/}
        {otherOwnerCpfOverview && otherOwnerCpfOverview?.length > 0 && (
          <Separator
            variant='dashed'
            style={{
              marginTop: `${loginOwnerCpfOverview && loginOwnerCpfOverview?.withdrawalRequests?.length > 0 ? '1.5rem' : ''}`,
              marginBottom: '1.5rem',
            }}
          />
        )}

        {/* Other Owner's Display Cards */}
        {otherOwnerCpfRequests?.map((otherOwner, index) => {
          // Check if selected other owner has been notified already
          const otherOwnerNotifiedStatus = proposedOwnersNotifiedStatus?.find(
            (notification: { uinfin: string }) => PolymerCrypto.decryption(notification.uinfin) === otherOwner.nric
          );

          // Check if the login owner is pending any forms him/herself.
          // Only show option to notify other owners if he/she has completed.
          const hasLoginOwnerCompletedWithdrawal = loginOwnerCpfOverviewPendingRequests?.length === 0;

          return (
            <div key={index} style={{ marginBottom: '1.5rem' }}>
              {(Object.entries(otherOwner.successRequests).length > 0 || Object.entries(otherOwner.pendingRequests).length > 0) && (
                <P2TextLabel bold={700} label={`CPF withdrawal by ${otherOwner.nric}`} />
              )}

              {/* Display successed withdrawal */}
              {Object.entries(otherOwner.successRequests).map(([changeId, { totalAmount, latestUpdate }]) => (
                <div key={changeId} style={{ marginBottom: '1.5rem' }}>
                  <StageEInfoCard
                    variant='success'
                    cardLabel={'CPF withdrawal e-Service has been completed'}
                    title={`$ ${Helpers.formatThousandSeparator(totalAmount)}`}
                    description={`Completed on: ${Helpers.formatDate(latestUpdate)}`}
                  />
                </div>
              ))}

              {/* Display pending withdrawal, either pre notified or notified */}
              {Object.entries(otherOwner.pendingRequests).map(([changeId, { totalAmount, latestUpdate }]) => (
                <div key={changeId} style={{ marginBottom: '1.5rem' }}>
                  <StageEInfoCard
                    variant='info'
                    notifyStatus={hasLoginOwnerCompletedWithdrawal ? otherOwnerNotifiedStatus?.cpf_e_serviced_notified : undefined}
                    cardLabel={'Amount to be withdrawn'}
                    title={`$ ${Helpers.formatThousandSeparator(totalAmount)}`}
                    description={
                      hasLoginOwnerCompletedWithdrawal
                        ? 'Notify the other owners to complete their CPF withdrawal form before the completion appointment date.'
                        : ''
                    }
                  />
                </div>
              ))}
            </div>
          );
        })}

        {cpfWithdrawalOverview && cpfWithdrawalOverview.parties.length === 0 && (
          <div>
            <P2Alert variant={'info'} withBorder={true} text={'There is no CPF withdrawal required based on the latest financial plan.'} />
          </div>
        )}
      </P2Accordion>
    </div>
  );
};

export default CPFWithdrawalEService;
